import { useMemo } from 'react';
import { isNil } from 'lodash';
import type { DashboardChartSeries } from 'api/models/DashboardChartSeries';
import type { DashboardComponentData } from 'api/models/DashboardComponentData';
import type { DashboardDataResponse } from 'api/models/DashboardDataResponse';
import type { DashboardMetricValue } from 'api/models/DashboardMetricValue';
import { useStore } from 'common/store';
import { useFirmMeasurementDateFilter } from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import { useIntelligenceContext } from 'pages/Intelligence/IntelligenceContext';
import {
  type SummaryDashboardType,
  useGetDashboardData,
} from 'services/hooks/summaryDefinitions';
import { getObjectValue, isValidDate } from 'utilities';
import type {
  ComparisonMode,
  ComponentDataStatus,
  DashboardDataView,
  MetricAggregation,
  ResolvedChartSeries,
  ResolvedComparison,
  ResolvedComponentData,
  ResolvedMetric,
} from './dashboardData.types';

interface UseDashboardDataParams {
  definitionId: number;
  summaryType?: SummaryDashboardType;
  // Empty dashboards render the prompt starter and have nothing to resolve.
  isEnabled?: boolean;
}

const toComparison = (metric: DashboardMetricValue): ResolvedComparison | null => {
  const {comparison} = metric;
  if (!comparison || comparison.status !== 'ok') {
    return null;
  }
  return {
    absoluteChange: comparison.delta ?? null,
    entityCount: comparison.entity_count ?? 0,
    mode: comparison.mode as ComparisonMode,
    // delta_percent is already a fraction on the wire. Normalizing here is the single place to
    // change if that ever becomes a percentage.
    relativeChange: comparison.delta_percent ?? null,
  };
};

const toResolvedMetric = (metric: DashboardMetricValue): ResolvedMetric => ({
  aggregation: metric.aggregation as MetricAggregation,
  comparison: toComparison(metric),
  contributingCount: metric.contributing_count ?? 0,
  label: metric.label,
  metric: metric.metric,
  unsupportedReason: metric.unsupported_reason ?? null,
  value: metric.value ?? null,
});

const toChartSeries = (series: DashboardChartSeries): ResolvedChartSeries => ({
  aggregation: series.aggregation as MetricAggregation,
  label: series.label,
  metric: series.metric,
  unsupportedReason: series.unsupported_reason ?? null,
  values: series.values ?? [],
});

const toComponentData = (component: DashboardComponentData): ResolvedComponentData => ({
  categories: component.categories ?? [],
  id: component.id,
  metrics: (component.metrics ?? []).map(toResolvedMetric),
  series: (component.series ?? []).map(toChartSeries),
  status: component.status as ComponentDataStatus,
  unsupportedReason: component.unsupported_reason ?? null,
});

const buildComponentMap = (
  response: DashboardDataResponse | null | undefined
): Map<string, ResolvedComponentData> => {
  const entries = response?.components ?? [];
  return new Map(
    entries
      // Component types without a data contract come back as unsupported_component with no
      // metrics; leaving them out of the map is what keeps their placeholder rendering.
      .filter(component => Boolean(component.id) && component.status !== 'unsupported_component')
      .map(component => [component.id, toComponentData(component)])
  );
};

// Bridges the dashboard to the scoping controls that stay on screen while a dashboard tab is
// active — the measurement-date chip in the firm shell and the fund filter in the Intelligence
// secondary nav — and adapts the wire response into the card view model.
//
// The date has no context of its own: it lives in the `?date=` query param and every consumer,
// including the chip itself, calls useFirmMeasurementDateFilter directly.
export const useDashboardData = ({
  definitionId,
  summaryType = 'firm',
  isEnabled = true,
}: UseDashboardDataParams): DashboardDataView => {
  const [storeValue] = useStore();
  const currentFirm = getObjectValue(getObjectValue(storeValue).firmInfo);
  const firmId = Number(currentFirm.id) || undefined;

  const { isLoadingMeasurementDate, isMostRecentMeasurementDate, selectedMeasurementDate } =
    useFirmMeasurementDateFilter();
  const { isAllFundsSelected, selectedFundId } = useIntelligenceContext();

  // Mirrors useFirmSummaryTab so a dashboard tab and a spreadsheet tab resolve the same date.
  // "Most Recent" must send nothing: the sentinel's date is a slug rather than a real date, and
  // sending the newest date explicitly would both fragment the cache and pin the backend to one
  // date instead of letting it resolve the latest snapshot per company.
  const resolvedMeasurementDate =
    !isNil(selectedMeasurementDate) && isValidDate(selectedMeasurementDate.date)
      ? selectedMeasurementDate.date
      : undefined;
  const measurementDate = isMostRecentMeasurementDate ? undefined : resolvedMeasurementDate;

  const { data, isError, isLoading } = useGetDashboardData({
    definitionId,
    firmId,
    fundId: selectedFundId,
    measurementDate,
    // Waiting on the fund map matters: without it the first request goes out unscoped and returns
    // firm-wide numbers, which then visibly jump when the fund-scoped response lands.
    shouldQueryAutomatically:
      isEnabled &&
      !isNil(firmId) &&
      !isNil(definitionId) &&
      !isLoadingMeasurementDate &&
      (isAllFundsSelected || !isNil(selectedFundId)),
    summaryType,
  });

  return useMemo(
    () => ({
      byComponentId: buildComponentMap(data),
      currency: data?.currency ?? 'USD',
      isCurrencyDegraded: Boolean(data?.fx_degraded),
      isError,
      isLoading,
    }),
    [data, isError, isLoading]
  );
};

export default useDashboardData;
